import {Component, Signal, computed} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {toSignal, toObservable} from '@angular/core/rxjs-interop';
import {Observable, catchError, combineLatest, distinctUntilChanged, EMPTY, expand, filter, map, merge, of, reduce, shareReplay, switchMap, timer} from 'rxjs';
import {VtkJobOrgService} from './vtk-job-org.service';
import {VtkResult, VtkService, vtkResult} from '../../../services/vtk.service';
import {VtkJobDetail, VtkJobMetrics, VtkLogEntry, VtkTask, VtkTaskMetrics, VTK_STATUS_LABELS, VTK_STATUS_COLORS} from '../../../services/vtk.types';
import {ContextService} from '../../../services/context.service';
import {Organization} from '../../../types-repository';
import {Airline} from '../../../types-workflow';
import {LogoAreaComponent, BreadCrumbs} from '../../logo-area/logo-area.component';
import {HeadlineSectionComponent} from '../../headline-section/headline-section.component';

@Component({
  selector: 'app-vtk-task-log',
  styles: [`
    :host {
      height: calc(100% - 45px);
      display: table;
    }
  `],
  template: `
    <app-logo-area [breadcrumbs]="breadCrumbs()"></app-logo-area>
    <main>
    <app-headline-section [title]="'Task ' + taskId()"></app-headline-section>
    <section class="cl-section cl-section-regular">
    <div class="container"><div class="row"><div class="col-12">

    @if (task()?.error; as error) {
      <p class="alert cl-alert-danger">{{ error }}</p>
    } @else {
      @if (task()?.value; as task) {
        <p class="cl-p-large">
          {{ jobId() }} — {{ task.tool }} ({{ task.id }})
          <span class="cl-badge cl-badge-sm" [class]="statusColor(task.status)">{{ statusLabel(task.status) }}</span>
        </p>
      }
      <p class="alert cl-alert-info">Auto-refreshes every 10s</p>

      @if (logs(); as logs) {
        @if (logs.error) {
          <p class="alert cl-alert-danger">Could not load the log of task {{ taskId() }}: {{ logs.error }}</p>
        } @else if (logs.value.length > 0) {
          <pre>@for (log of logs.value; track $index) {{{ log.message.trimEnd() }}
}</pre>
        } @else {
          <p>No log entries.</p>
        }
      } @else {
        <p>Loading ...</p>
      }
    }

    </div></div></div>
    </section>
    </main>
  `,
  imports: [LogoAreaComponent, HeadlineSectionComponent]
})
export class VtkTaskLogComponent {
  organization: Signal<Organization>;
  airline: Signal<Airline>;
  poName: Signal<string>;
  poItemId: Signal<string>;
  jobId: Signal<string>;
  taskId: Signal<string>;
  // The task as the job lists it. VTK answers an unknown task id's log with 200 and no entries, so the
  // job's own task list is what tells a wrong id apart from an empty log (VTK-3464).
  task: Signal<VtkResult<VtkTask> | undefined>;
  taskMetrics: Signal<VtkTaskMetrics | undefined>;
  // undefined until the first response; a failure is shown, not rendered as an empty log (VTK-3464).
  logs: Signal<VtkResult<VtkLogEntry[]> | undefined>;
  breadCrumbs: Signal<BreadCrumbs>;

  constructor(
    private route: ActivatedRoute,
    private vtkService: VtkService,
    private contextService: ContextService,
    private jobOrg: VtkJobOrgService,
  ) {
    this.organization = this.contextService.organization;
    this.airline = this.contextService.airline;
    this.poName = toSignal(this.route.params.pipe(map(p => p['po']), distinctUntilChanged()), {initialValue: ''});
    this.poItemId = toSignal(this.route.params.pipe(map(p => p['poitem']), distinctUntilChanged()), {initialValue: ''});
    this.jobId = toSignal(this.route.params.pipe(map(p => p['job_id']), distinctUntilChanged()), {initialValue: ''});
    this.taskId = toSignal(this.route.params.pipe(map(p => p['task_id']), distinctUntilChanged()), {initialValue: ''});

    // The VTK organization comes from the PO item's record of the job (VtkJobOrgService); an
    // org_urn query parameter on an old link is ignored. Each job is paired with its own lookup only:
    // a new job ends the previous one's requests, and a failed lookup leaves nothing polling.
    const job$ = this.jobOrg.jobOrgs(this.route).pipe(shareReplay({bufferSize: 1, refCount: true}));
    const taskId$ = this.route.params.pipe(map(p => p['task_id']), distinctUntilChanged());
    // Task ids are numbers; anything else would be requested as tasks/NaN/log. The task check above
    // reports such an id as not part of the job.
    const numericTaskId$ = taskId$.pipe(filter(id => /^\d+$/.test(id)));

    // Get task metadata from the job detail
    this.task = toSignal(
      combineLatest([job$, taskId$]).pipe(
        switchMap(([{org, organizationId, poName, jobId}, taskId]): Observable<VtkResult<VtkTask> | undefined> => {
          if (!org) return of(undefined);
          // Without the organization no request can be made: say so instead of "Loading ..." forever.
          if (org.error) return of({error: org.error});
          return vtkResult(this.vtkService.getJob(org.value!, organizationId, poName, jobId)).pipe(
            map((job): VtkResult<VtkTask> => {
              if (job.error) {
                return {error: `Could not load VTK job ${jobId}: ${job.error}`};
              }
              const task = job.value.tasks.find(t => String(t.id) === String(taskId));
              return task ? {value: task} : {error: `Task ${taskId} is not part of VTK job ${jobId}.`};
            })
          );
        })
      ),
    );

    // Get task-level metrics
    this.taskMetrics = toSignal(
      combineLatest([job$, taskId$]).pipe(
        switchMap(([{org, organizationId, poName, jobId}, taskId]) => org?.value
          ? this.vtkService.getJobMetrics(org.value, organizationId, poName, jobId).pipe(
            map(m => m.tasks.find(t => String(t.task_id) === String(taskId))),
            catchError(() => of(undefined))
          )
          : of(undefined))
      )
    );

    // Poll logs every 10s, fetching all pages
    this.logs = toSignal(
      combineLatest([job$, numericTaskId$]).pipe(
        switchMap(([{org, organizationId, poName, jobId}, taskId]) => org?.value
          ? timer(0, 10000).pipe(
            switchMap(() => this.fetchAllLogs(org.value!, organizationId, poName, jobId, Number(taskId)))
          )
          : of(undefined))
      )
    );

    this.breadCrumbs = computed(() => {
      const org_id = this.organization()?.id;
      const airline = this.airline();
      const po = this.poName();
      const poItemId = this.poItemId();
      const jobId = this.jobId();
      if (!org_id || !airline) return [];
      return [
        {display: 'Content Platform', commands: ['/o', org_id]},
        {display: 'Workflows', commands: ['/o', org_id, 'workflows']},
        {display: airline.airline_name, commands: ['/o', org_id, 'workflows', airline.iata_code]},
        {display: po, commands: ['/o', org_id, 'workflows', airline.iata_code, po]},
        {display: poItemId, commands: ['/o', org_id, 'workflows', airline.iata_code, po, poItemId]},
        {display: `Job ${jobId}`, commands: ['/o', org_id, 'workflows', airline.iata_code, po, poItemId, 'vtk-jobs', jobId]},
      ];
    });
  }

  private fetchAllLogs(ownerOrg: string, viewAs: string, jobBundle: string, jobId: string, taskId: number) {
    const limit = 1000;
    return vtkResult(this.vtkService.getTaskLog(ownerOrg, viewAs, jobBundle, jobId, taskId, 0, limit).pipe(
      expand(res => {
        const fetched = res.offset + res.results.length;
        return fetched < res.count
          ? this.vtkService.getTaskLog(ownerOrg, viewAs, jobBundle, jobId, taskId, fetched, limit)
          : EMPTY;
      }),
      reduce((all, res) => all.concat(res.results), [] as VtkLogEntry[]),
    ));
  }

  statusLabel(status: number): string { return VTK_STATUS_LABELS[status] ?? 'Unknown'; }
  statusColor(status: number): string { return VTK_STATUS_COLORS[status] ?? 'cl-color-ash'; }
}
